import { useMemo, useState } from "react";
import type { FC, ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { EChart as RawEChart } from "@kbox-labs/react-echarts";

import BudgetBar from "./BudgetBar";
import {
  BudgetDetail, STATUS_BG, STATUS_COLOR, STATUS_TEXT, budgetChartOption, dayLabel, fmtBudget, fmtUsd,
  followersTooltip, monthName, monthsChartOption, statusChipLabel, summaryBar, summaryParts,
} from "./budgets";

const EChart = RawEChart as unknown as FC<any>;

// Spend on a scale that grows past 100 % with the forecast: ticks at the thresholds,
// a hollow dot at the forecast.
function SummaryBar({ budget }: { budget: BudgetDetail }) {
  const bar = summaryBar(budget.pct, budget.forecast_pct, budget.thresholds);
  return (
    <Box sx={{ position: "relative", height: 10, bgcolor: "#eef0f3", borderRadius: 1, my: 1 }}>
      <Box sx={{ width: `${bar.fill}%`, height: "100%", bgcolor: STATUS_COLOR[budget.status], borderRadius: 1 }} />
      {bar.ticks.map((t) => (
        <Box key={t} sx={{ position: "absolute", left: `${t}%`, top: -3, width: "1px", height: 16, bgcolor: "#9ca3af" }} />
      ))}
      {bar.forecast !== null && (
        <Box title="forecast" sx={{ position: "absolute", left: `calc(${bar.forecast}% - 6px)`, top: -1, width: 12,
          height: 12, boxSizing: "border-box", borderRadius: "50%", border: "2px solid #1565c0", bgcolor: "#fff" }} />
      )}
    </Box>
  );
}

function Legend() {
  const item = (swatch: ReactNode, label: string) => (
    <Stack key={label} direction="row" spacing={0.5} alignItems="center">
      {swatch}
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
  const box = (color: string) => <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }} />;
  const line = (style: string) => <Box sx={{ width: 14, borderTop: `2px ${style} #1565c0` }} />;
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      {item(box("#90caf9"), "per day")}
      {item(line("solid"), "total")}
      {item(line("dashed"), "forecast")}
      {item(box("#ed6c02"), "thresholds")}
    </Stack>
  );
}

export default function BudgetDetailPanel({ budget, busy, onToggleFollow, onEdit, onDelete }: {
  budget: BudgetDetail; busy: boolean; onToggleFollow: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const currentMonth = budget.months[budget.months.length - 1]?.month ?? budget.start_date;
  const month = monthName(currentMonth);
  const option = useMemo(() => budgetChartOption(budget, currentMonth), [budget, currentMonth]);
  const history = useMemo(() => monthsChartOption(budget.months, budget.thresholds), [budget]);
  const maxCost = Math.max(1, ...budget.breakdown.map((b) => b.cost));
  const started = budget.budget_usd !== null && budget.pct !== null;

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" noWrap>{budget.name}</Typography>
            <Chip size="small" label={statusChipLabel(budget)}
              sx={{ bgcolor: STATUS_BG[budget.status], color: STATUS_TEXT[budget.status], fontWeight: 600 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {[budget.note, `created by ${budget.created_by}`,
              budget.updated_by ? `edited by ${budget.updated_by}${budget.updated_at ? ` on ${dayLabel(budget.updated_at)}` : ""}` : "",
              `thresholds ${budget.thresholds.join(" / ")} %`]
              .filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Tooltip title={followersTooltip(budget.follower_emails)}>
            <span>
              <Button size="small" variant="outlined" disabled={busy} onClick={onToggleFollow}
                startIcon={budget.following ? <StarIcon sx={{ color: "#f59e0b" }} /> : <StarBorderIcon />}>
                {budget.following ? "Following" : "Follow"}
              </Button>
            </span>
          </Tooltip>
          <Button size="small" variant="outlined" onClick={onEdit}>Edit</Button>
          <IconButton size="small" aria-label="More actions" onClick={(e) => setMenu(e.currentTarget)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
            <MenuItem onClick={() => { setMenu(null); onDelete(); }}>Delete budget</MenuItem>
          </Menu>
        </Stack>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.1 }}>{fmtUsd(budget.spend_usd)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {started ? `of ${fmtBudget(budget.budget_usd as number)} · ${month}` : `budget ${fmtBudget(budget.amount_usd)} / month`}
          </Typography>
        </Stack>
        <SummaryBar budget={budget} />
        <Typography variant="caption" color="text.secondary">{summaryParts(budget, currentMonth).join(" · ")}</Typography>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mt: 2.5 }}>
        <Box sx={{ flex: 3, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
            <Typography variant="subtitle2">{month}</Typography>
            <Legend />
          </Stack>
          <EChart style={{ width: "100%", height: 220 }} {...option} />
        </Box>
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Typography variant="subtitle2">Last 6 months</Typography>
          <EChart style={{ width: "100%", height: 220 }} {...history} />
        </Box>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Where the money goes ({month})</Typography>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Customer</TableCell><TableCell>Category</TableCell>
              <TableCell align="right">Spent</TableCell><TableCell /></TableRow>
          </TableHead>
          <TableBody>
            {budget.breakdown.map((b, i) => {
              const customer = b.organization ? `${b.organization} → ${b.customer}` : "";
              const previous = i > 0 && budget.breakdown[i - 1].organization
                ? `${budget.breakdown[i - 1].organization} → ${budget.breakdown[i - 1].customer}` : "";
              return (
                <TableRow key={`${customer}-${b.category}`}>
                  <TableCell>{customer === previous ? "" : customer}</TableCell>
                  <TableCell>{b.category}</TableCell>
                  <TableCell align="right">{fmtUsd(b.cost)}</TableCell>
                  <TableCell sx={{ width: 120 }}><BudgetBar pct={(b.cost / maxCost) * 100} color="#7c9cc9" /></TableCell>
                </TableRow>
              );
            })}
            {budget.breakdown.length === 0 && (
              <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">
                No costs this month yet.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
